import { useNavigate, useLocation } from "react-router-dom";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Fade,
  Grow,
  Box,
  Typography,
  Button,
  useTheme,
  Skeleton,
} from "@mui/material";
import { PlayArrow } from "@mui/icons-material";
import AIProfilesCarousel from "../components/AIProfilesCarousel";
import { getPitchTypes } from "../services/pitchTypesService";
import PitchTypeSelector from "../components/PitchTypeSelector";
import AppBackground from "../components/layout/AppBackground";
import SessionAlertDialog from "../components/PitchSession/SessionAlertDialog";
import { useAuth } from "../hooks/AuthContext";

const PitchSessionStart = () => {
  const { t, i18n } = useTranslation("pitch_start");
  const [pitchTypes, setPitchTypes] = useState([]);
  const [selectedPitchType, setSelectedPitchType] = useState(null);
  const [selectedAIProfile, setSelectedAIProfile] = useState(null);
  const [startError, setStartError] = useState("");
  const [blockedOptionNames, setBlockedOptionNames] = useState([]);
  const handleProfileSelect = useCallback((profile) => {
    setSelectedAIProfile(profile);
    setStartError("");
    setBlockedOptionNames([]);
  }, []);
  const lang = i18n.language?.startsWith("es") ? "es" : "en";
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      const data = await getPitchTypes();
      setPitchTypes(data);

      // Reasignar pitchType si viene de PitchSession
      const matchedPitch = location.state?.pitchType
        ? data.find((pt) => pt.id === location.state.pitchType.id)
        : null;

      setSelectedPitchType(matchedPitch || data[0]);

      // Asignar AIProfile si viene de PitchSession
      if (location.state?.aiProfile) {
        setSelectedAIProfile(location.state.aiProfile);
      }
    };

    fetchData();
  }, []);

  return (
    <Fade in timeout={300}>
      <AppBackground
        sx={{
          py: 5,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: { xs: "95%", sm: "100%", md: 800 },
            px: { xs: 1, sm: 2, md: 3 },
            mx: "auto",
            overflowX: "hidden",
          }}
        >
          {/* Title */}
          <Box maxWidth="600px" width="100%">
            <Typography variant="h4" gutterBottom sx={{ py:0, mt: 0, mb: 5 }}>
              {t("title")}
            </Typography>
          </Box>

          <Grow in timeout={400}>
          {/* Pitch Type Selector */}
          {/* <Box sx={{ mb: 8 }}>
            <Typography
              variant="h5"
              gutterBottom
              textAlign="left"
              sx={{
                mb: 3,
                color: theme.palette.text.primary,
                textShadow: "0 0 6px rgba(0, 0, 0, 0.4)",
              }}
            >
              {t("select_pitch_type")}
            </Typography>
            {pitchTypes.map((pt) => (
              <PitchTypeSelector
                key={pt.id}
                pitchType={pt}
                selected={pt.id === selectedPitchType?.id}
                onSelect={() => setSelectedPitchType(pt)}
                lang={lang}
              />
            ))}
          </Box> */}
          <Box sx={{ mb: 8, minHeight: { xs: 120, md: 100 } }}>
            <Typography
              variant="h5"
              gutterBottom
              textAlign="left"
              sx={{
                mb: 3,
                color: theme.palette.text.primary,
                textShadow: "0 0 6px rgba(0, 0, 0, 0.4)",
              }}
            >
              {t("select_pitch_type")}
            </Typography>

            {pitchTypes.length === 0 ? (
              <>
                {[...Array(3)].map((_, i) => (
                  <Skeleton
                    key={i}
                    variant="rounded"
                    height={80}
                    sx={{
                      mb: 2,
                      borderRadius: 3,
                      backgroundColor: "rgba(255,255,255,0.08)",
                    }}
                  />
                ))}
              </>
            ) : (
              pitchTypes.map((pt) => (
                <PitchTypeSelector
                  key={pt.id}
                  pitchType={pt}
                  selected={pt.id === selectedPitchType?.id}
                  showBlocked={isAuthenticated}
                  onSelect={() => {
                    setSelectedPitchType(pt);
                    setStartError("");
                    setBlockedOptionNames([]);
                  }}
                  lang={lang}
                />
              ))
            )}
          </Box>

          </Grow>
          {/* AI Profiles Carousel */}
          <Grow in timeout={700}>
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              textAlign="left"
              sx={{
                mb: 3,
                color: theme.palette.text.primary,
                textShadow: "0 0 6px rgba(0, 0, 0, 0.4)",
              }}
            >
              {t("select_ai_profile")}
            </Typography>
            <Box>
              <AIProfilesCarousel
                imageSizeMultiplier={1.5}
                onSelectProfile={handleProfileSelect}
                initialProfileId={location.state?.aiProfile?.id}
                showBlocked={isAuthenticated}
              />
            </Box>
          </Box>
          </Grow>

          {/* Start Button */}
          <Grow in timeout={1000}>
          <Box textAlign="center" mb={8}>
            <Typography
              variant="body2"
              sx={{
                mb: 2,
                color: "rgba(255,255,255,0.7)",
              }}
            >
              {t("start_microcopy")}
            </Typography>

            <Button
              disabled={!selectedAIProfile || !selectedPitchType}
              onClick={() => {
                const blockedNames = isAuthenticated ? [
                  selectedPitchType?.blocked
                    ? selectedPitchType[`name_${lang}`]
                    : null,
                  selectedAIProfile?.blocked
                    ? selectedAIProfile[`name_${lang}`]
                    : null,
                ].filter(Boolean) : [];
                if (blockedNames.length > 0) {
                  setBlockedOptionNames(blockedNames);
                  setStartError(t("blocked_option", { name: blockedNames.join(" + ") }));
                  return;
                }
                setStartError("");
                navigate("/pitch", {
                  state: {
                    pitchType: selectedPitchType,
                    aiProfile: selectedAIProfile,
                  },
                });
              }}
              variant="contained"
              size="large"
              sx={{
                borderRadius: "999px",
                px: 4,
                py: 1.5,
                fontWeight: 600,
                textTransform: "none",
                color: "#ffffff",
                background: "linear-gradient(to right, #5e35b1, #7e57c2)",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
                "&:hover": {
                  background: "linear-gradient(to right, #4527a0, #673ab7)",
                },
              }}
              startIcon={<PlayArrow />}
            >
              {t("start")}
            </Button>
            <SessionAlertDialog
              open={Boolean(startError)}
              onClose={() => setStartError("")}
              onConfirm={() => setStartError("")}
              title={t("blocked_title")}
              description={
                blockedOptionNames.length > 0 ? (
                  <>
                    {t("blocked_option_before")}
                    <strong>{blockedOptionNames.join(" + ")}</strong>
                    {t("blocked_option_after")}
                  </>
                ) : startError
              }
              confirmText={t("blocked_close")}
            />
          </Box>
          </Grow>
        </Box>
      </AppBackground>
    </Fade>
  );
};

export default PitchSessionStart;
